import { useAccount } from "@qw/auth";
import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";

const RECENT_SEARCHES_STORAGE_KEY_PREFIX = "qwib-recent-searches";

/**
 * Keyed per inbox, which is what keeps the list honest under impersonation: a broker working
 * someone else's mailbox (`impersonation-menu.tsx`) searches that inbox, so they see that inbox's
 * history rather than the names the previous occupant of the browser typed.
 */
export const recentSearchesStorageKey = (inboxId: string) => `${RECENT_SEARCHES_STORAGE_KEY_PREFIX}:${inboxId}`;

/**
 * How many terms come back. Small deliberately: the band shares an already-crowded dropdown with the
 * scope offer, a list long enough to scroll is a list nobody reads, and removal is one term at a
 * time — so the cap is also what keeps clearing the whole list by hand a short job.
 */
const RECENT_SEARCH_LIMIT = 5;

// One identity for the empty list, because `useLocalStorage` takes it as the initial value and
// re-reads whenever that value changes — a fresh `[]` per render would re-read on every render.
const NO_RECENT_SEARCHES: string[] = [];

/**
 * Narrow on read: this value has been sitting in a browser across deploys and can be anything, so a
 * shape we do not recognise is dropped rather than rendered — the same contract the layout
 * preferences use in `use-inbox-list-thread-layout.ts`. The cap is applied here too, so a list
 * written by some older build cannot render longer than the band is meant to be.
 */
const asRecentSearches = (stored: unknown): string[] =>
  Array.isArray(stored)
    ? stored
        .filter((term): term is string => typeof term === "string" && term.trim().length > 0)
        .slice(0, RECENT_SEARCH_LIMIT)
    : NO_RECENT_SEARCHES;

/**
 * The terms a broker has actually used in the search box, newest first.
 *
 * **Device-local on purpose.** These are the names of insureds, agents and people the broker typed
 * looking for mail, and the app has already decided once that this text does not leave the machine:
 * `posthog-tracker.tsx` strips `location.search` off every pageview for exactly that reason. Keeping
 * the list in `localStorage` puts it in the same class as the drafts already in the browser, and
 * means no retention question has to be answered to ship it.
 */
export const useRecentSearches = () => {
  const { userInbox } = useAccount();
  const inboxId = userInbox?.id;

  // `useLocalStorage` cannot be called conditionally, so a render without an inbox still needs a
  // key. Nothing ever writes to this one — every mutation below returns early without an inbox — and
  // the hook re-reads whenever the key changes, which is what makes switching inboxes switch lists.
  const [stored, setStored] = useLocalStorage<string[]>(
    inboxId ? recentSearchesStorageKey(inboxId) : `${RECENT_SEARCHES_STORAGE_KEY_PREFIX}:none`,
    NO_RECENT_SEARCHES
  );

  /**
   * A term the broker *used* — committed, or typed on the way to picking a row. Not every keystroke:
   * the dropdown reacts to typing, so recording that would fill the list with prefixes of the one
   * search they meant.
   *
   * No minimum length. `MINIMUM_SUGGESTION_TERM_LENGTH` gates the suggestion *queries*, not commits,
   * so a one-character Enter is still a search that ran and still worth offering back.
   */
  const recordRecentSearch = useCallback(
    (term: string) => {
      const trimmed = term.trim();

      if (!inboxId || !trimmed) {
        return;
      }

      // Case-insensitive dedupe keeping the newest spelling: a broker who searched "Acme" and then
      // "acme" wants one row, and the one they just typed is the one they recognise. Capped on write
      // as well as on read, so the oldest term is dropped rather than kept offscreen.
      setStored((current) =>
        [
          trimmed,
          ...asRecentSearches(current).filter((existing) => existing.toLowerCase() !== trimmed.toLowerCase()),
        ].slice(0, RECENT_SEARCH_LIMIT)
      );
    },
    [inboxId, setStored]
  );

  const removeRecentSearch = useCallback(
    (term: string) => {
      if (!inboxId) {
        return;
      }

      setStored((current) => asRecentSearches(current).filter((existing) => existing !== term));
    },
    [inboxId, setStored]
  );

  return {
    recentSearches: inboxId ? asRecentSearches(stored) : NO_RECENT_SEARCHES,
    recordRecentSearch,
    removeRecentSearch,
  };
};
